'use client';
import React, { forwardRef } from 'react';

import { Recaptcha, type RecaptchaHandle, type RecaptchaProps } from './Recaptcha';
import { useRecaptchaSiteKey } from './useRecaptchaSiteKey';

export type { RecaptchaHandle };

export type PayloadRecaptchaProps = Omit<RecaptchaProps, 'siteKey'> & {
  /** URL de l'endpoint exposant la clé (défaut : /api/recaptcha-config). */
  configUrl?: string;
};

/**
 * Variante « clés en main » pour les apps **Payload CMS** : récupère la clé de
 * site au runtime via l'endpoint `/api/recaptcha-config` (ajouté par
 * `recaptchaPlugin`) puis délègue au composant contrôlé `Recaptcha`. Le
 * formulaire n'a rien à savoir de la clé — il pose
 * `<PayloadRecaptcha ref requireRecaptcha />` et lit `ref.current?.getValue()`.
 *
 * Dans un projet Next.js sans cet endpoint Payload, utiliser directement
 * `Recaptcha` en lui passant `siteKey` (ou `useRecaptchaSiteKey` avec une autre
 * `configUrl`).
 */
export const PayloadRecaptcha = forwardRef<
  RecaptchaHandle,
  PayloadRecaptchaProps
>(function PayloadRecaptcha({ requireRecaptcha, className, configUrl }, ref) {
  const siteKey = useRecaptchaSiteKey({
    enabled: !!requireRecaptcha,
    configUrl,
  });

  return (
    <Recaptcha
      ref={ref}
      siteKey={siteKey}
      requireRecaptcha={requireRecaptcha}
      className={className}
    />
  );
});
